import {createSlice} from '@reduxjs/toolkit'
import {isVaultHeightValid} from "../../validation/form/planFormValidation"
import {initialVaultState} from "./initialVaultState"
import {create as createStockVaultState, validSizes as validStockVaultSizes} from "./stockVaultStateFactory"

const vaultSlice = createSlice({
    name: 'vault',
    initialState: null,
    reducers: {
        setVault: () => initialVaultState,
        unsetVault: () => null,
        setStockVault: (state) => {
            return (
                !!state && state.isStockVault && false
                    ? state :
                    createStockVaultState(validStockVaultSizes[2])
            )
        },
        unsetStockVault: () => null,
        setStockVaultSize: (state, action) => {
            if (!!state
                && validStockVaultSizes.includes(action.payload)
                && state.selectedSize !== action.payload
            ) {
                return createStockVaultState(action.payload);
            }
        },
        setVaultHeight: (state, action) => {
            // payload: { height }
            const {height} = action.payload

            if (Number.isInteger(height) && isVaultHeightValid(height)) {
                state.height = height
            }
        },
        // === Sleeves actions ===
        addSleeve: (state, action) => {
            // payload: { wallIndex, sleeve }
            const {wallIndex, sleeve} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].sleeves.push(sleeve)
            }
        },
        removeSleeve: (state, action) => {
            // payload: { wallIndex, id }
            const {wallIndex, id} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].sleeves = state.walls[wallIndex].sleeves.filter(sleeve => sleeve.id !== id)
            }
        },
        removeSleeves: (state, action) => {
            // payload: Array<{ wallIndex: number, id: string }>
            const targets = action.payload;

            const byWall = targets.reduce((acc, { wallIndex, id }) => {
                (acc[wallIndex] ||= new Set()).add(id);
                return acc;
            }, ({}));

            for (const [wallIdxStr, idSet] of Object.entries(byWall)) {
                const wallIdx = Number(wallIdxStr);
                const wall    = state.walls[wallIdx];
                if (!wall) continue;

                wall.sleeves = wall.sleeves.filter(s => !idSet.has(s.id));
            }
        },
        moveSleeve: (state, action) => {
            // payload: { wallIndex, id, x, y }
            const {wallIndex, id, x, y} = action.payload

            if (state.walls[wallIndex]) {
                const sleeve = state.walls[wallIndex].sleeves.find(s => s.id === id)
                if (sleeve) {
                    sleeve.x = x
                    sleeve.y = y
                }
            }
        },
        // === BlockOuts actions ===
        addBlockOut: (state, action) => {
            // payload: { wallIndex, blockOut }
            const {wallIndex, blockOut} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].blockOuts.push(blockOut)
            }
        },
        removeBlockOut: (state, action) => {
            // payload: { wallIndex, id }
            const {wallIndex, id} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].blockOuts = state.walls[wallIndex].blockOuts.filter(b => b.id !== id)
            }
        },
        moveBlockOut: (state, action) => {
            // payload: { wallIndex, id, x, y }
            const {wallIndex, id, x, y} = action.payload

            if (state.walls[wallIndex]) {
                const blockOut = state.walls[wallIndex].blockOuts.find(b => b.id === id)
                if (blockOut) {
                    blockOut.x = x
                    blockOut.y = y
                }
            }
        },
        resizeBlockOut: (state, action) => {
            // payload: { wallIndex, id, width, height }
            const {wallIndex, id, width, height} = action.payload

            if (state.walls[wallIndex]) {
                const blockOut = state.walls[wallIndex].blockOuts.find(b => b.id === id)
                if (blockOut) {
                    blockOut.width = width
                    blockOut.height = height
                }
            }
        },
        // === Wall Drainages actions ===
        addWallDrainage: (state, action) => {
            // payload: { wallIndex, drainage }
            const {wallIndex, drainage} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].drainages.push(drainage)
            }
        },
        removeWallDrainage: (state, action) => {
            // payload: { wallIndex, id }
            const {wallIndex, id} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].drainages = state.walls[wallIndex].drainages.filter(d => d.id !== id)
            }
        },
        moveWallDrainage: (state, action) => {
            // payload: { wallIndex, id, x, y }
            const {wallIndex, id, x, y} = action.payload

            if (state.walls[wallIndex]) {
                const drainage = state.walls[wallIndex].drainages.find(d => d.id === id)
                if (drainage) {
                    drainage.x = x
                    drainage.y = y
                }
            }
        },
        // === Groundings actions ===
        addGrounding: (state, action) => {
            // payload: { wallIndex, grounding }
            const {wallIndex, grounding} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].groundings.push(grounding)
            }
        },
        removeGrounding: (state, action) => {
            // payload: { wallIndex, id }
            const {wallIndex, id} = action.payload
            if (state.walls[wallIndex]) {
                state.walls[wallIndex].groundings = state.walls[wallIndex].groundings.filter(g => g.id !== id)
            }
        },
        moveGrounding: (state, action) => {
            // payload: { wallIndex, id, x, y }
            const {wallIndex, id, x, y} = action.payload

            if (state.walls[wallIndex]) {
                const grounding = state.walls[wallIndex].groundings.find(g => g.id === id)
                if (grounding) {
                    grounding.x = x
                    grounding.y = y
                }
            }
        },
        // === C-Rails actions ===
        addCRail: (state, action) => {
            // payload: { wallIndex, cRail }
            const {wallIndex, cRail} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].cRails.push(cRail)
            }
        },
        removeCRail: (state, action) => {
            // payload: { wallIndex, id }
            const {wallIndex, id} = action.payload

            if (state.walls[wallIndex]) {
                state.walls[wallIndex].cRails = state.walls[wallIndex].cRails.filter(c => c.id !== id)
            }
        },
        moveCRail: (state, action) => {
            // payload: { wallIndex, id, x, y }
            const {wallIndex, id, x, y} = action.payload

            if (state.walls[wallIndex]) {
                const cRail = state.walls[wallIndex].cRails.find(c => c.id === id)
                if (cRail) {
                    cRail.x = x
                    cRail.y = y
                }
            }
        },
        resizeCRail: (state, action) => {
            // payload: { wallIndex, id, length }
            const {wallIndex, id, length} = action.payload

            if (state.walls[wallIndex]) {
                const cRail = state.walls[wallIndex].cRails.find(c => c.id === id)
                if (cRail) {
                    cRail.length = length
                }
            }
        },
        // === Mirror wall action ===
        mirrorFromOppositeSide: (state, action) => {
            // payload: { wallIndex, mirroredWall }
            // Only walls 1 and 3 can be mirrored
            const {wallIndex, mirroredWall} = action.payload

            if (wallIndex !== 1 && wallIndex !== 3) return
            state.walls[wallIndex] = mirroredWall;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(setStockVault, () => null);
    }
})

export const {
    setVault,
    unsetVault,
    setStockVault,
    unsetStockVault,
    setStockVaultSize,
    setVaultHeight,
    addSleeve,
    removeSleeve,
    removeSleeves,
    moveSleeve,
    addBlockOut,
    removeBlockOut,
    moveBlockOut,
    resizeBlockOut,
    addWallDrainage,
    removeWallDrainage,
    moveWallDrainage,
    addGrounding,
    removeGrounding,
    moveGrounding,
    addCRail,
    removeCRail,
    moveCRail,
    resizeCRail,
    mirrorFromOppositeSide
} = vaultSlice.actions

export default vaultSlice.reducer